import { type ChangeEvent, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { CADTABLE_DEBOUNCE_TIME } from "~/utils/constants";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import classes from "~/features/productDetails/components/Cad/CadTable/CadTable.module.less";

import { isCADInquiryEnabled } from "~/utils/features";
import { Row } from "~/common/components/Layout";
import classNames from "classnames";
import DataTable from "~/common/components/Table/DataTable";
import { userIsRegistered } from "~/domains/user/utils/roles";
import { type DropdownEntry } from "~/common/types";
import {
  type CadFormDefinition,
  CadFormFieldType,
  type FieldDefinition,
} from "~/features/productDetails/components/Cad/CadTypes";
import { type SelectChangeEvent, TextField } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";

interface CadTableProps {
  data: CadFormDefinition;
  onChange: (newValue: [string, string]) => void;
}

export const CadForm = ({ data, onChange: onChangeExternal }: CadTableProps) => {
  const { t } = useTranslation();
  const [currentValues, setCurrentValues] = useState<Record<string, string>>({});

  useLayoutEffect(() => {
    setCurrentValues(generateCurrentValues(data));
  }, [data]);

  const onChange = useMemo(() => {
    return debounce(onChangeExternal, CADTABLE_DEBOUNCE_TIME);
  }, [onChangeExternal]);

  const makeFieldProps = (field: FieldDefinition) => {
    return {
      onChange: (value: string | ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | SelectChangeEvent<unknown>) => {
        const newValue = typeof value === "string" ? value : (value.target.value as string);
        const newValues = { ...currentValues, [field.symbol]: newValue };
        setCurrentValues(newValues);
        onChange([field.symbol, newValue]);
        if (field.type === CadFormFieldType.Select) {
          onChange.flush();
        }
      },
      onBlur: () => {
        onChange.flush();
      },
      value: currentValues[field.symbol] ?? "",
    };
  };

  return (
    <>
      {data && data.length > 0 && (
        <Row className={classes.root}>
          <span className={classes.title}>{t("page.pdp.cad.title")}</span>
          <div className={classNames("col-md-8", classes.tableContainer)}>
            <DataTable.Table theme={"stripes"}>
              {data.map((field, index) => (
                <DataTable.CellRow key={field.symbol}>
                  <DataTable.Cell className={classes.tableCell}>
                    <div className={classes.tableContainerLabel}>
                      <span className={classes.tableLabel}>{field.label}</span>
                      <span className={classes.tableLabelHelper}>{getHelperLabel(field)}</span>
                    </div>
                  </DataTable.Cell>
                  <DataTable.Cell className={classNames(classes.tableCell, classes.tableOptions)}>
                    {field.type === CadFormFieldType.Select && (
                      <SimpleSelect
                        className={classes.tableDropdown}
                        data-test-id={"scha__cad-table__dropdown" + index}
                        fullWidth={true}
                        values={getOptions(field)}
                        {...makeFieldProps(field)}
                      />
                    )}
                    {field.type === CadFormFieldType.Text && (
                      <span className={classes.tableOptionText}>{currentValues[field.symbol]}</span>
                    )}
                    {field.type === CadFormFieldType.Number && (
                      <TextField
                        fullWidth
                        inputProps={{ "data-test-id": "scha__cad-table__input" + index }}
                        className={"scha__cad-table__input"}
                        autoComplete={"off"}
                        {...makeFieldProps(field)}
                      />
                    )}
                  </DataTable.Cell>
                </DataTable.CellRow>
              ))}
            </DataTable.Table>
            {userIsRegistered() && isCADInquiryEnabled() && (
              <Alert severity={"info"} className={"mt-30"}>
                {t("page.pdp.cad.messageInfo")}
              </Alert>
            )}
          </div>
        </Row>
      )}
    </>
  );
};

function generateCurrentValues(data: CadFormDefinition) {
  const currentValues: Record<string, string> = {};
  for (const item of data) {
    let value;
    if (item.type === CadFormFieldType.Text || item.type === CadFormFieldType.Number) {
      value = item.valuesFieldGrid[0].value;
    } else if (item.type === CadFormFieldType.Select) {
      value = item.valuesFieldGrid.find((ele) => ele.selected === "selected")?.value;
    }
    currentValues[item.symbol] = value ?? "";
  }
  return currentValues;
}

function getHelperLabel(field: FieldDefinition) {
  return field.valuesFieldGrid[0]?.helpLabel;
}

function getOptions(field: FieldDefinition): DropdownEntry<string>[] {
  return field.valuesFieldGrid.map((item) => ({
    label: item.label,
    value: item.value,
  }));
}
